import { ReactNode } from "react";
import { componentConfig } from "config/theme";
import { Text } from "./Text";

export type SportTabItem<Key extends string | number> = {
  key: Key;
  label: ReactNode;
  disabled?: boolean;
};

export type SportTabsProps<Key extends string | number> = {
  tabs: SportTabItem<Key>[];
  activeKey: Key;
  onChange: (key: Key) => void;
  align?: "start" | "center" | "end";
  className?: string;
  "data-testid"?: string;
};

export function SportTabs<Key extends string | number>({
  tabs,
  activeKey,
  onChange,
  align = "start",
  className,
  "data-testid": dataTestId,
}: SportTabsProps<Key>) {
  const alignment =
    align === "start"
      ? "justify-start"
      : align === "end"
      ? "justify-end"
      : "justify-center";
  const wrapperClasses = ["flex w-full pb-2", alignment, className]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={wrapperClasses} data-testid={dataTestId} data-align={align}>
      <div className={componentConfig.sportTabsWrapperClass}>
        {tabs.map((tab) => {
          const isActive = tab.key === activeKey;
          const buttonClasses = [
            componentConfig.sportTabsButtonBaseClass,
            componentConfig.sportTabsButtonHeightClass,
            tab.disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
            isActive
              ? componentConfig.sportTabsButtonActiveColorClass
              : componentConfig.sportTabsButtonInactiveColorClass,
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <button
              key={String(tab.key)}
              type="button"
              onClick={() => !tab.disabled && onChange(tab.key)}
              disabled={tab.disabled}
              className={buttonClasses}
            >
              <Text
                variant="sportTabLabel"
                className="inline-flex items-center gap-2 transition-all duration-200"
              >
                {tab.label}
              </Text>
            </button>
          );
        })}
      </div>
    </div>
  );
}
